import {
  Dispatch,
  SetStateAction,
} from 'react';
import { useRecoilValue } from 'recoil';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Form, Button } from 'react-bootstrap';
import {
  PDPMatchData,
  MatchAction,
  ActionData,
  MDPData,
} from '../types/mdp.types';
import {
  FormatLocalDate,
  tryParseInt,
  handleActionDisplay,
  renderMatchStatus,
} from '../utils';
import {
  STATUS_CHOICES,
  PDP_DISCERNMENT_ACTIONS,
} from '../types/constants';
import { Role } from '../types/user.types';
import { currentRoleState } from '../services/state.service';
import MDPService from '../services/mdp.service';

type PDPMatchRowProp = {
  index: number,
  displayCallSeekerName?: boolean,
  match: PDPMatchData,
  pdpMatches: PDPMatchData[],
  setPDPMatches: Dispatch<SetStateAction<PDPMatchData[]>>,
  setActionData: Dispatch<SetStateAction<ActionData>>,
  mdp: MDPData,
  matchActionOptions: MatchAction[],
  setShowConfirmSubmit: Dispatch<SetStateAction<boolean>>,
  setShowNotifyCall: Dispatch<SetStateAction<boolean>>,
  setShowMDPNotes: Dispatch<SetStateAction<boolean>>,
  setSelectedMatch: Dispatch<SetStateAction<PDPMatchData | undefined>>,
  state: string,
}

function PDPMatchRow(
  {
    index,
    displayCallSeekerName,
    match,
    pdpMatches,
    setPDPMatches,
    setActionData,
    mdp,
    matchActionOptions,
    setShowConfirmSubmit,
    setShowNotifyCall,
    setSelectedMatch,
    setShowMDPNotes,
    state,
  }: PDPMatchRowProp,
): JSX.Element {
  const position = pdpMatches.find((pt) => pt.pdp === match.pdp);
  const location = useLocation();
  const currentRole = useRecoilValue<Role>(currentRoleState);
  const { t } = useTranslation();

  const handleActionChange = (pdpId: number, actionId: number | undefined, status: string): void => {
    setPDPMatches((p: any) => (
      p.map((pt: any) => {
        if (pt.pdp === position?.pdp) {
          return { ...pt, selectedAction: actionId, selectedActionKey: status };
        }
        return pt;
      })
    ));
  };

  const matchActionMenu = handleActionDisplay(matchActionOptions, match, pdpMatches)?.map((option: MatchAction) => (
    <option key={`match-${option.id}`} value={option.id}>
      {option.description || ''}
    </option>
  ));

  function submitAction(pos: PDPMatchData | undefined): void {
    if (pos) {
      setActionData((p : any) => ({
        ...p,
        mdpId: mdp.id,
        pdpId: pos.pdp,
        matchId: pos.id,
        action: pos.selectedAction,
        actionKey: pos.selectedActionKey,
      }));

      if (pos?.selectedActionKey === PDP_DISCERNMENT_ACTIONS.beingConsidered.notifyACall) {
        setShowNotifyCall(true);
      } else {
        setShowConfirmSubmit(true);
      }
    }
  }

  const renderCompatibilityCircle = (level: number) => {
    switch (level) {
      case 1:
        return <span className="circle green-circle" />;
      case 2:
        return <span className="circle yellow-circle" />;
      case 3:
        return <span className="circle red-circle" />;
      default:
        return null;
    }
  };

  const viewedMatch = () => {
    MDPService.UpdatePDPMatch({ ...match, newMatch: false });
  };

  return (
    <tr key={`pdp-${match.id}`} className={`${match.newMatch && 'new-match'} dividinglines`}>
      <td>
        {index}
      </td>
      <td>
        <Link
          onClick={viewedMatch}
          to={location.pathname
            .replace('matches', 'pdpdetails')
            .concat(`?pdp_id=${match.pdp}&match_id=${match.id}&mdp_id=${match.mdpId}&state=${state}`)}
        >
          {displayCallSeekerName ? (
            <span>
              {`${match.pdp} - ${match.callSeekerName}`}
            </span>
          ) : (
            <span>
              {match.pdp}
            </span>
          )}
        </Link>
      </td>
      <td>{match.createdAt > '1900-01-01' ? FormatLocalDate(match.createdAt) : ''}</td>
      <td>
        <table>
          <tbody>
            {match.positionTypes && match.positionTypes.length > 0 ? (
              match.positionTypes.map((list) => (
                <tr key={`position-type-${list}`}>
                  <td>{list}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td>
                  {match.positionTypes ? (
                    t('No_Experience')
                  ) : (
                    ''
                  )}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </td>
      <td>{renderMatchStatus(match, t, false)}</td>
      <td>
        {renderCompatibilityCircle(match.compatibilityLevel)}
      </td>
      <td>
        {currentRole.abbreviation !== 'PNC Member' && (
          <>
            <div className="d-inline-block mb-1 me-3">
              <Form.Group>
                <Form.Select
                  value={position?.selectedAction || ''}
                  onChange={(e) => handleActionChange(
                    match.pdp,
                    tryParseInt(e.target.value),
                    e.target.options[e.target.selectedIndex].text,
                  )}
                >
                  {match.statusKey !== (STATUS_CHOICES.callCompleted || STATUS_CHOICES.acceptedCall)
                    ? (
                      <>
                        <option key="" value="">
                          {t('Select_Action')}
                        </option>
                        {matchActionMenu}
                      </>
                    ) : (
                      <option key="" value="">
                        {t('No_Available_Actions')}
                      </option>
                    )}
                </Form.Select>
              </Form.Group>
            </div>
            <div className="d-inline-block mb-1 me-3">
              <Button
                onClick={() => submitAction(position)}
                className="createbutton"
                variant="primary"
                disabled={
                  position?.selectedAction === undefined
                  || position?.selectedAction === 0
                }
                active
              >
                {`${t('Submit_Action')}`}
              </Button>
            </div>
          </>
        )}
        <div className="d-inline-block mb-1 me-3">
          <Button
            type="button"
            className="me-4 ms-auto createbutton note-btn"
            variant="primary"
            active
            onClick={() => {
              setSelectedMatch(match);
              setShowMDPNotes(true);
            }}
          >
            {t('View_Edit_Notes')}
          </Button>
        </div>
      </td>
    </tr>
  );
}

PDPMatchRow.defaultProps = {
  displayCallSeekerName: false,
};

export default PDPMatchRow;
